'use client';

import type { FC } from 'react';
import { buttonVariants, P } from '@paypoteam/ui-components';
import Image from 'next/image';
import Link from 'next/link';

import { shopReferral } from '@/lib/gtm';
import { useCssDimensions } from '@/utils/hooks/useCssDimensions';
import mapPrismicImageProxy from '@/utils/prismic/mapPrismicImageProxy';

import type { LocalImageProps } from '@/components/atoms/LocalImage';
import H2 from '@/components/atoms/Typography/H2';
import type { SectionProps } from '@/components/layout/Section';
import Section from '@/components/layout/Section';

import type { HeroBannerDataType } from './CroppedImageSection.types';

export interface CroppedImageSectionProps extends SectionProps {
    heroBannerData: HeroBannerDataType;
    shopLinkPlacement?: string;
    partnerName?: string;
    customImage?: LocalImageProps;
    className?: string;
}

const CroppedImageSection: FC<CroppedImageSectionProps> = ({
    heroBannerData,
    partnerName,
    customImage,
    shopLinkPlacement,
    ...sectionProps
}) => {
    const {
        imageUrl,
        title,
        description,
        buttonLink,
        buttonText,
        isButtonVisible,
        partnerLogo,
        logotypeUrl,
        ...props
    } = heroBannerData;

    const logo = partnerLogo || logotypeUrl;

    const { minWidthMd } = useCssDimensions();

    return (
        <Section
            {...props}
            {...sectionProps}
            containerClassName="max-w-[1168px] relative md:min-h-[500px] md:flex justify-between items-center md:flex-row"
        >
            {minWidthMd && (
                <div className="right-0 top-0 z-0 hidden h-[200px] max-h-[420px] w-full md:absolute md:block md:h-full md:max-h-[500px] md:max-w-[835px]">
                    <Image
                        src={imageUrl || customImage?.src || ''}
                        alt="Zdjęcie dla kategorii"
                        fill
                        priority
                    />
                </div>
            )}

            <div className="relative z-10 py-12 text-center md:min-h-[420px] md:max-w-[360px] md:bg-white md:pr-8 md:text-left lg:max-w-[560px]">
                {!!logo && (
                    <Image
                        className="mx-auto my-0 md:mx-0"
                        width={150}
                        height={65}
                        src={mapPrismicImageProxy(logo)}
                        alt="Logo partnera"
                    />
                )}

                <H2 className="pb-4 md:text-left md:text-3xl lg:text-5xl">
                    {title}
                </H2>

                {!!description &&
                    (Array.isArray(description) ? (
                        description?.map((paragraph, index) => (
                            <P
                                key={'cropped-image-paragraph-' + index}
                                className="mb-5"
                                dangerouslySetInnerHTML={{
                                    __html: paragraph.text,
                                }}
                            />
                        ))
                    ) : (
                        <P className="mb-5">{description}</P>
                    ))}

                {(!!isButtonVisible || !!buttonLink) && (
                    <Link
                        href={buttonLink || ''}
                        className={buttonVariants({
                            variant: 'primary',
                            className: 'mx-auto my-0 w-full sm:w-fit md:mx-0',
                        })}
                        target="_blank"
                        onClick={() => {
                            if (partnerName && shopLinkPlacement) {
                                shopReferral(partnerName, shopLinkPlacement);
                            }
                        }}
                        data-test-id="cropped-image-section-button-link"
                    >
                        {buttonText || 'Przejdź do sklepu'}
                    </Link>
                )}
            </div>
        </Section>
    );
};

export default CroppedImageSection;
